<template>
    <div class="modal-container">
        <div class="modal-header">
            <span v-if="selectedCustomField === null">Add Stop Custom Field</span>
            <span v-if="selectedCustomField !== null">Edit {{ selectedCustomField.label }}</span>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.form" class="form-wrapper">
                <form-group v-if="selectedCustomField !== null" label="Id" name="Id">
                    <md-input v-model="form.name" disabled />
                </form-group>
                <form-group label="Label" name="label">
                    <md-input v-model="form.label" />
                </form-group>
                <form-group name="type">
                    <vue-select
                        v-if="customFieldTypes"
                        :options="customFieldTypes"
                        placeholder="Type"
                        v-model="form.type"
                        :searchable="$root.isDesktop"
                    ></vue-select>
                </form-group>

                <div v-if="optionBasedFieldTypes.includes(form.type)" class="md-layout md-size-100 options">
                    <div class="md-layout-item md-size-100">
                        <form-group name="options">
                            <md-input v-model="optionValue" placeholder="Option" @keyup.enter="addOption" />
                        </form-group>
                    </div>
                    <div class="md-layout-item md-size-0 add-icon" @click="addOption">
                        <md-icon>add</md-icon>
                    </div>
                </div>
                <div v-if="optionBasedFieldTypes.includes(form.type) && form.options.length" class="options-table">
                    <label>Available Options:</label>
                    <md-table>
                        <md-table-row v-for="(option, index) in form.options" :key="index">
                            <md-table-cell>
                                <form-group>
                                    <md-input v-model="option.value" />
                                </form-group>
                            </md-table-cell>
                            <md-table-cell v-if="form.showAsBadge" class="color-picker-cell">
                                <div class="color-swatch-row">
                                    <span
                                        v-for="palette in badgeColorPalette"
                                        :key="palette.key"
                                        class="color-swatch"
                                        :class="{ 'is-selected': option.color === palette.hex }"
                                        :style="{ backgroundColor: palette.hex }"
                                        :title="palette.key"
                                        @click="selectPaletteColor(index, palette.hex)"
                                    ></span>
                                    <span
                                        class="color-swatch custom-swatch"
                                        :class="{ 'is-selected': isCustomColor(option.color) }"
                                        title="Custom"
                                        @click="customHexEditingIndex = customHexEditingIndex === index ? null : index"
                                    >
                                        <md-icon>colorize</md-icon>
                                    </span>
                                    <md-input
                                        v-if="customHexEditingIndex === index"
                                        v-model="option.color"
                                        placeholder="#RRGGBB"
                                        class="custom-hex-input"
                                        :class="{ 'is-invalid': option.color && !isValidHex(option.color) }"
                                    />
                                </div>
                            </md-table-cell>
                            <md-table-cell>
                                <md-button
                                    class="md-just-icon md-danger md-simple"
                                    title="delete option"
                                    @click.native="removeOption(index)"
                                >
                                    <md-icon>close</md-icon>
                                </md-button>
                            </md-table-cell>
                        </md-table-row>
                    </md-table>
                </div>

                <div>
                    <md-checkbox v-model="form.isRequired">
                        Required
                    </md-checkbox>
                </div>
                <div>
                    <md-checkbox v-model="form.includeInPodPdf">
                        Include in Proof Of Delivery PDF
                    </md-checkbox>
                </div>
                <div v-if="badgeEligibleFieldTypes.includes(form.type)">
                    <md-checkbox v-model="form.showAsBadge">
                        Show as badge
                    </md-checkbox>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button v-if="selectedCustomField === null" class="dialog-button md-primary" @click="addStopCustomField">
                Add
            </md-button>
            <md-button
                v-if="selectedCustomField !== null"
                class="dialog-button md-primary"
                @click="editStopCustomField"
            >
                Save
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">
                Cancel
            </md-button>
        </div>
    </div>
</template>

<script>
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
    CUSTOM_FIELD_TYPES_CONSTANTS,
    BADGE_ELIGIBLE_FIELD_TYPES,
    OPTION_BASED_FIELD_TYPES,
    BADGE_COLOR_PALETTE,
    HEX_COLOR_REGEX
} from '@/utils/constants';

export default {
    name: 'StopCustomFieldModal',
    mixins: [GeneralMixin],
    props: {
        selectedCustomField: {
            type: Object,
            default: null
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    data() {
        return {
            form: {
                name: '',
                label: '',
                type: '',
                isRequired: false,
                includeInPodPdf: false,
                showAsBadge: false,
                options: []
            },
            optionValue: null,
            customHexEditingIndex: null,
            customfield: {},
            customFieldTypes: CUSTOM_FIELD_TYPES_CONSTANTS,
            badgeEligibleFieldTypes: BADGE_ELIGIBLE_FIELD_TYPES,
            optionBasedFieldTypes: OPTION_BASED_FIELD_TYPES,
            badgeColorPalette: BADGE_COLOR_PALETTE
        };
    },
    mounted() {
        if (this.selectedCustomField !== null) {
            this.form.name = this.selectedCustomField.name;
            this.form.label = this.selectedCustomField.label;
            this.form.type = this.selectedCustomField.type;
            this.form.isRequired = this.selectedCustomField.isRequired;
            this.form.includeInPodPdf = this.selectedCustomField.includeInPodPdf;
            this.form.showAsBadge = this.selectedCustomField.showAsBadge === true;
            this.form.assetType = this.selectedCustomField.assetType;
            this.form.options = this.selectedCustomField.options || [];
        }
    },
    methods: {
        isDuplicateOption(value) {
            const index = this.form.options.findIndex((x) => x.value.toLowerCase() === value.toLowerCase());
            return index === -1 ? false : index;
        },
        showWarningDuplicate(value) {
            this.$notify({
                message: `Option name ${value} already exists.`,
                type: 'warning'
            });
        },
        addOption() {
            if (this.optionValue != null) {
                if (!this.form.options) {
                    this.form.options = [{ value: this.optionValue }];
                    this.optionValue = null;
                } else if (!this.isDuplicateOption(this.optionValue)) {
                    this.form.options.push({ value: this.optionValue });
                    this.optionValue = null;
                } else {
                    this.showWarningDuplicate(this.optionValue);
                }
            }
        },
        removeOption(index) {
            this.form.options.splice(index, 1);
        },
        addStopCustomField() {
            this.$v.form.$touch();
            if (!this.$v.form.$invalid) {
                if (this.hasInvalidOptionColors()) {
                    this.$notify({
                        message:
                            'One or more option colours have an invalid hex code. Please correct them before saving.',
                        type: 'warning'
                    });
                    return;
                }
                this.customfield = {
                    name: this.form.label.toLowerCase().replace(/[^\w]/gi, ''),
                    label: this.form.label,
                    type: this.form.type,
                    isRequired: this.form.isRequired,
                    includeInPodPdf: this.form.includeInPodPdf,
                    showAsBadge: this.form.showAsBadge,
                    options: this.form.options
                };
                this.resolve(this.customfield);
            }
        },
        editStopCustomField() {
            this.$v.form.$touch();
            if (!this.$v.form.$invalid) {
                if (this.hasInvalidOptionColors()) {
                    this.$notify({
                        message:
                            'One or more option colours have an invalid hex code. Please correct them before saving.',
                        type: 'warning'
                    });
                    return;
                }
                this.customfield = Object.assign({}, this.form);
                this.resolve(this.customfield);
            }
        },
        selectPaletteColor(index, hex) {
            const current = this.form.options[index].color;
            this.$set(this.form.options[index], 'color', current === hex ? null : hex);
        },
        isCustomColor(color) {
            if (!color) {
                return false;
            }
            return !this.badgeColorPalette.some((p) => p.hex.toLowerCase() === color.toLowerCase());
        },
        isValidHex(value) {
            return HEX_COLOR_REGEX.test(value);
        },
        hasInvalidOptionColors() {
            return this.form.options.some((o) => o.color && !this.isValidHex(o.color));
        }
    },
    validations: {
        form: {
            label: {
                required
            },
            type: {
                required
            },
            options: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function() {
                        return this.optionBasedFieldTypes.includes(this.form.type);
                    })
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.color-picker-cell {
    .color-swatch-row {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        flex-wrap: wrap;
    }
    .color-swatch {
        display: inline-block;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        cursor: pointer;
        border: 2px solid transparent;
        box-sizing: content-box;

        &.is-selected {
            border-color: #333;
        }
        &.custom-swatch {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f5f5f5;
            .md-icon {
                font-size: 14px !important;
            }
        }
    }
    .custom-hex-input {
        max-width: 90px;
        font-family: monospace;
        font-size: 0.85rem;
        &.is-invalid {
            border-bottom-color: #d32f2f;
        }
    }
}
</style>
